import Alert from "@mui/material/Alert";
import type { ReactNode } from "react";
import type { ProposalCollaborationEnvelope } from "work/proposals/negotiation/proposal-collaboration-contract";
import {
  proposalPendingReviewWarning,
  type StagedProposalReviewerChanges,
} from "work/proposals/_views/_shared/proposal-pending-review-warning";

/**
 * The pending-review warning as every confirmation that commits the party draws it: Submit, Submit
 * Changes, Approve, Approve & Hire and Hire (#6635).
 *
 * Those confirmations used to keep three rules. Submit showed this warning; Hire counted reviewers its
 * own way and asked a differently worded question; Approve & Hire, the most binding of the three, said
 * nothing while the header showed reviews pending. One component over one count keeps the badge and
 * every confirmation in agreement. It informs and never gates: the action stays available (#5128).
 */
export default function ProposalPendingReviewAlert({ message }: Readonly<{ message: string | undefined }>) {
  if (!message) return null;

  return (
    <Alert severity="warning" sx={{ mt: 2 }}>
      {message}
    </Alert>
  );
}

/**
 * A lifecycle confirmation's question, with the warning below it when the viewing party's reviewers
 * have not all approved the current terms. The bare question when they have, so the dialog reads as
 * it always did, and when the negotiation has not loaded: with no envelope there is no approval to
 * count, and the action itself reports that it is not ready.
 *
 * A reviewer the leader has staged for removal is not counted: the leader has already decided their
 * review does not count, and Approve saves the removal before it acts (#6869).
 */
export function proposalCommitmentConfirmationMessage(
  question: string,
  envelope: ProposalCollaborationEnvelope | undefined,
  stagedReviewerChanges?: StagedProposalReviewerChanges,
): ReactNode {
  const warning = envelope ? proposalPendingReviewWarning(envelope, stagedReviewerChanges) : undefined;
  if (!warning) return question;

  return (
    <>
      {question}
      <ProposalPendingReviewAlert message={warning} />
    </>
  );
}
